import React from 'react';
import { TableCell, TableRow } from '@mui/material';
import type { UsageRouteRow } from '../types';
import { LEER, formatCount, formatDuration, formatNumber, formatPercent } from '../services/usageFormat';
import SchutzZeile from './SchutzZeile';
import DatenTabelle, { zahlenZelle } from './DatenTabelle';

/**
 * RITA-1505 (Nutzungserfassung Phase 2c) — meistgenutzte Seiten.
 *
 * Routen sind Muster ohne Nummern ('/agent/:number'), sonst wäre an der Adresse ein einzelner
 * Berater ablesbar. Das 95. Perzentil fehlt unter zehn belegten Pseudonymen — es beschriebe eine
 * Einzelperson; liegt es über der letzten Histogramm-Kante, steht die Textmarke des Servers
 * ('> 12,8 s') statt einer erfundenen Zahl.
 */
export interface SeitenTabelleProps {
  rows: UsageRouteRow[];
}

/** Nutzerzahl; '≥' macht eine Untergrenze sichtbar, wenn ältere Pseudonymlisten gelöscht sind. */
export const nutzerText = (users: number, usersEstimated: boolean): string =>
  usersEstimated ? `≥ ${formatCount(users)}` : formatCount(users);

/** Scrolltiefe kommt als Prozentzahl (0..100), nicht als Anteil — nicht noch einmal mal 100. */
const scrollText = (value: number | null): string => (value === null ? LEER : `${formatNumber(value, 0)} %`);

/** Perzentil oder die Textmarke des Servers; beides ist eine Aussage, eine Null wäre keine. */
const perzentilText = (ms: number | null, note: string | null): string => {
  if (ms !== null) return formatDuration(ms);
  return note ?? LEER;
};

export default function SeitenTabelle({ rows }: SeitenTabelleProps) {
  return (
    <DatenTabelle
      leer={rows.length === 0}
      leerText="Im Zeitraum sind keine Seitenaufrufe verdichtet."
      spalten={[
        { titel: 'Route' },
        { titel: 'Aufrufe', numerisch: true },
        { titel: 'Nutzer', numerisch: true },
        { titel: 'Ø Verweildauer', numerisch: true },
        { titel: 'Median', numerisch: true },
        { titel: '95 %', numerisch: true },
        { titel: 'Ø Scrolltiefe', numerisch: true },
        { titel: 'Rücksprung', numerisch: true },
        { titel: 'Fehler', numerisch: true },
      ]}
    >
      {rows.map((row, stelle) => (
        // Ein Schlüssel aus der Route trüge an Sammelzeilen nichts; die Reihenfolge des Servers
        // ist fest (nach Aufrufen), deshalb ist die Stelle hier der stabile Schlüssel.
        // eslint-disable-next-line react/no-array-index-key
        <TableRow key={`${row.label}-${stelle}`}>
          <TableCell>
            <SchutzZeile
              suppressed={row.suppressed}
              groups={row.groups}
              users={row.users}
              bezeichner={row.route}
              technisch
            />
          </TableCell>
          <TableCell sx={zahlenZelle}>{formatCount(row.views)}</TableCell>
          <TableCell sx={zahlenZelle}>{nutzerText(row.users, row.usersEstimated)}</TableCell>
          <TableCell sx={zahlenZelle}>{formatDuration(row.avgVisibleMs)}</TableCell>
          <TableCell sx={zahlenZelle}>{formatDuration(row.p50VisibleMs)}</TableCell>
          <TableCell sx={zahlenZelle}>{perzentilText(row.p95VisibleMs, row.p95Note)}</TableCell>
          <TableCell sx={zahlenZelle}>{scrollText(row.avgScrollPct)}</TableCell>
          <TableCell sx={zahlenZelle}>{formatPercent(row.bounceRate)}</TableCell>
          <TableCell sx={zahlenZelle}>{formatCount(row.errors)}</TableCell>
        </TableRow>
      ))}
    </DatenTabelle>
  );
}
